import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { RestoreRegionSelect } from '@/components/regions/restoreRegionSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreDynamoState } from '../restoreDynamoWizard';

export const DynamoConfiguration = (props: StepProps<RestoreDynamoState>) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      regionName: state.initialRegionName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  const canGoNext = () => {
    if (
      isRestoring ||
      !props.wizardState.regionName ||
      !props.wizardState.restoredName ||
      !getEncryptionKey(props.wizardState.encryption)
    ) {
      return false;
    }

    return true;
  };

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const tags = props.wizardState.tags || {};
    tags['eon:restore'] = 'true';

    return dal.restore
      .dynamoDB(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          awsDynamodb: {
            tags,
            encryptionKeyId:
              getEncryptionKey(props.wizardState.encryption) || '',
            restoredName: props.wizardState.restoredName || '',
            restoreRegion: props.wizardState.regionName || '',
            writeCapacityUnits: props.wizardState.writeCapacityUnits || 40_000,
          },
        },
      })
      .then(() => props.finish('Restore job has started!'))
      .catch((e) => {
        setError(`Failed to restore Dynamo table: ${e.message}`);
      })
      .finally(() => {
        setIsRestoring(false);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <FormControl size='small' className='flex-1'>
            <FormLabel>Table name</FormLabel>
            <TextField
              data-testid='databaseNameTextField'
              size='small'
              value={props.wizardState.restoredName || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  restoredName: event.target.value,
                }))
              }
            />
          </FormControl>
          <RestoreRegionSelect
            cloudProvider={Provider.Aws}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(region) =>
              props.setWizardState((state) => ({
                ...state,
                regionName: region,
              }))
            }
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <FormControl size='small' className='flex-1'>
            <FormLabel>Write Capacity Units</FormLabel>
            <TextField
              data-testid='databaseNameTextField'
              size='small'
              value={props.wizardState.writeCapacityUnits || ''}
              type='number'
              inputProps={{ step: '1', min: '0' }}
              placeholder='40000'
              variant='outlined'
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  writeCapacityUnits: Number(event.target.value) || undefined,
                }))
              }
            />
          </FormControl>
        </Stack>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        {props.wizardState.regionName && (
          <EncryptionComponent
            encryption={props.wizardState.encryption}
            accountId={props.wizardState.restoreAccount!.id}
            regionName={props.wizardState.regionName}
            onChange={(encryption) =>
              props.setWizardState((state) => ({
                ...state,
                encryption,
              }))
            }
          />
        )}
      </CardContent>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={{}}
          tags={props.wizardState.tags || {}}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
